<script setup lang="ts">
import type { OverlayScrollbars } from "overlayscrollbars"

import { OverlayScrollbarsComponent } from "overlayscrollbars-vue"

import { getNativeScrollbarWidth } from "~/utils/platform"

const props = defineProps({
  vertical: { type: Boolean },
  horizontal: { type: Boolean },
  virtualHorizontal: { type: Boolean },
  size: null,
  id: null,
  observeMutations: { type: Boolean }
})
const emit = defineEmits(["scroll", "initialized"])

const nativeContainer = useTemplateRef("nativeContainer")
const overlayContainer = useTemplateRef("overlayContainer")

// We use the native scrollbar if we have a mac-style auto-hiding scrollbar that
// disappears automatically. Otherwise, we use our own custom one
const native = getNativeScrollbarWidth() === 0
const theme = props.size
  ? {
      sm: "os-theme-sm",
      md: "os-theme-md",
    }[props.size]
  : "os-theme-md"

const onNativeScroll = (event: Event) => {
  const target = event.currentTarget! as HTMLDivElement
  emit("scroll", {
    left: target.scrollLeft,
    top: target.scrollTop,
    right: target.scrollWidth - target.clientWidth - target.scrollLeft,
    bottom: target.scrollHeight - target.clientHeight - target.scrollTop,
  })
}

const onMutation = () => {
  let target
  if (native)
    target = nativeContainer.value
  else
    target = overlayContainer.value!.osInstance()?.elements().viewport
  if (!isDefined(target))
    return console.warn("No target for mutation callback")
  emit("scroll", {
    left: target.scrollLeft,
    top: target.scrollTop,
    right: target.scrollWidth - target.clientWidth - target.scrollLeft,
    bottom: target.scrollHeight - target.clientHeight - target.scrollTop,
  })
}
const observer = new MutationObserver(onMutation)

const onWheel = (event: WheelEvent) => {
  if (!props.horizontal || !props.virtualHorizontal)
    return
  if (!event.deltaY || event.deltaX)
    return
  const target = event.currentTarget as HTMLDivElement
  target.scrollLeft += event.deltaY
  emit("scroll", {
    left: target.scrollLeft,
    top: target.scrollTop,
    right: target.scrollWidth - target.clientWidth - target.scrollLeft,
    bottom: target.scrollHeight - target.clientHeight - target.scrollTop,
  })
}

const scrollTo = ({ left, top, bottom, right, behavior }: {
  left?: number
  top?: number
  bottom?: number
  right?: number
  behavior?: "smooth" | "auto"
}) => {
  if (isDefined(left) && isDefined(right))
    throw new Error("Cannot set both left and right scroll position")
  if (isDefined(top) && isDefined(bottom))
    throw new Error("Cannot set both top and bottom scroll position")

  if (nativeContainer.value) {
    const width = nativeContainer.value.scrollWidth
    const height = nativeContainer.value.scrollHeight
    top = top ?? (height - (bottom ?? 0))
    left = left ?? (width - (right ?? 0))
    nativeContainer.value.scrollTo({ left, top, behavior })
  }
  else {
    const os = overlayContainer.value?.osInstance()
    if (!os)
      return console.warn("OverlayScrollbars not initialized yet")
    const element = os.elements().viewport
    const width = element.scrollWidth - element.clientWidth
    const height = element.scrollHeight - element.clientHeight
    top = top ?? (height - bottom!)
    left = left ?? (width - right!)
    os.elements().viewport.scrollTo({ left, top, behavior })
  }
}

const onOverlayScroll = (overlay: OverlayScrollbars) => {
  const target = overlay.elements().viewport
  emit("scroll", {
    left: target.scrollLeft,
    top: target.scrollTop,
    right: target.scrollWidth - target.clientWidth - target.scrollLeft,
    bottom: target.scrollHeight - target.clientHeight - target.scrollTop,
  })
}

const onOverlayScrollbarInitialized = async (os: OverlayScrollbars) => {
  os.elements().scrollOffsetElement.id = props.id ?? ""
  os.elements().scrollOffsetElement.addEventListener("wheel", onWheel)
  if (props.observeMutations)
    observer.observe(os.elements().viewport, { childList: true, subtree: true })
  await nextTick()
  onMutation()
  emit("initialized")
}

onMounted(() => {
  if (nativeContainer.value) {
    if (props.observeMutations)
      observer.observe(nativeContainer.value, { childList: true, subtree: true })
    onMutation()
    emit("initialized")
  }
})

onUnmounted(() => {
  observer.disconnect()
})

defineExpose({ scrollTo })
</script>

<template>
  <div
    v-if="native"
    :id
    ref="nativeContainer"
    class="scrollbar-container-native"
    :class="{ vertical, horizontal }"
    @scroll="(event) => onNativeScroll(event)"
    @wheel.passive="(event) => onWheel(event)"
  >
    <slot />
  </div>
  <overlay-scrollbars-component
    v-else
    ref="overlayContainer"
    defer
    class="scrollbar-container-overlay"
    :options="{
      scrollbars: {
        theme,
        autoHide: 'move',
        autoHideDelay: 1000,
      },
      overflow: {
        x: horizontal ? 'scroll' : 'hidden',
        y: vertical ? 'scroll' : 'hidden',
      },
    }"
    :events="{
      scroll: onOverlayScroll,
      initialized: onOverlayScrollbarInitialized,
    }"
  >
    <slot />
  </overlay-scrollbars-component>
</template>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

.os-theme-md
  --os-size: 8px
  --os-handle-border-radius: 8px
  --os-handle-bg: #{$page-bg-scrollbar-default}
  --os-handle-bg-hover: #{$page-bg-scrollbar-hovered}
  --os-handle-bg-active: #{$page-bg-scrollbar-hovered}
  --os-padding-perpendicular: 2px
  --os-padding-axis: 2px
  --os-handle-perpendicular-size-hover: 8px
  --os-handle-interactive-area-offset: 4px

.os-theme-sm
  --os-size: 14px
  --os-handle-border-radius: 14px
  --os-handle-bg: #{$page-bg-scrollbar-default}
  --os-handle-bg-hover: #{$page-bg-scrollbar-hovered}
  --os-handle-bg-active: #{$page-bg-scrollbar-hovered}
  --os-padding-perpendicular: 5px
  --os-padding-axis: 5px
  --os-handle-perpendicular-size-hover: 6px

.scrollbar-container-overlay
  @apply h-full w-full

.scrollbar-container-native
  @apply h-full w-full
  &.vertical
    @apply overflow-y-auto
  &:not(.vertical)
    @apply overflow-y-hidden
  &.horizontal
    @apply overflow-x-auto
  &:not(.horizontal)
    @apply overflow-x-hidden
</style>
